import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import { COUNTRIES } from '../../shared/countries';
import { IconCheckCircle, IconInfo, IconMail } from '../../shared/icons';
import { AuthLayout } from './AuthLayout';

interface SignUpPageProps {
  onGoToSignIn: () => void;
}

/**
 * Self-service sign-up.
 *
 * Nothing is created here. The form hands the server an address, a password
 * and a short profile, and the account only comes into being when the link in
 * the resulting email is followed — which is what stops anyone registering a
 * colleague's address.
 */
export function SignUpPage({ onGoToSignIn }: SignUpPageProps) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [company, setCompany] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const submit = useMutation({
    mutationFn: () =>
      api.signup({
        email,
        display_name: displayName,
        password,
        company,
        country,
        city,
        phone,
      }),
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    submit.mutate();
  };

  // The server answers the same way whether or not the address is already
  // taken, and so does this screen. Saying "that address exists" here would
  // turn the form into an address checker.
  if (submit.isSuccess) {
    return (
      <AuthLayout kicker="New account" heading="Check your inbox">
        <div className="auth-card">
          <div className="notice notice-ok" role="status">
            <IconCheckCircle size={14} />
            <span>
              We sent a confirmation link to <strong>{email}</strong>. Follow it to finish creating
              your account.
            </span>
          </div>
          <p className="field-help">
            The link works once and expires in 24 hours. Until you follow it, no account exists.
            Nothing arrived? Check the spam folder.
          </p>
          <button className="btn btn-block" onClick={onGoToSignIn}>
            Back to sign in
          </button>
        </div>
      </AuthLayout>
    );
  }

  const error = submit.error instanceof ApiError ? submit.error : null;

  return (
    <AuthLayout kicker="New account" heading="Create an account">
      <form className="auth-card" onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>{error.message}</span>
          </div>
        )}
        {mismatch && (
          <div className="notice notice-error" role="alert">
            <IconInfo size={14} />
            <span>The two passwords do not match.</span>
          </div>
        )}

        <div className="field">
          <label className="lbl" htmlFor="signup-name">
            <span className="lbl-text">Your name</span>
          </label>
          <input
            id="signup-name"
            className="inp"
            type="text"
            autoComplete="name"
            required
            autoFocus
            placeholder="Jan de Vries"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="lbl" htmlFor="signup-email">
            <span className="lbl-text">Work email</span>
          </label>
          <input
            id="signup-email"
            className="inp"
            type="email"
            autoComplete="username"
            required
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <span className="field-help">
            A company address opens your account straight away. A personal one (Gmail, Outlook and
            the like) works too, but is checked by a person first.
          </span>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="signup-company">
            <span className="lbl-text">Company</span>
          </label>
          <input
            id="signup-company"
            className="inp"
            type="text"
            autoComplete="organization"
            required
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </div>

        <div className="field-row">
          <div className="field">
            <label className="lbl" htmlFor="signup-country">
              <span className="lbl-text">Country</span>
            </label>
            <select
              id="signup-country"
              className="inp"
              autoComplete="country-name"
              required
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            >
              <option value="">Select…</option>
              {COUNTRIES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="lbl" htmlFor="signup-city">
              <span className="lbl-text">City</span>
            </label>
            <input
              id="signup-city"
              className="inp"
              type="text"
              autoComplete="address-level2"
              required
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="signup-phone">
            <span className="lbl-text">Phone</span>
          </label>
          <input
            id="signup-phone"
            className="inp"
            type="tel"
            autoComplete="tel"
            required
            placeholder="+31 6 12345678"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="lbl" htmlFor="signup-password">
            <span className="lbl-text">Password</span>
          </label>
          <input
            id="signup-password"
            className="inp"
            type="password"
            autoComplete="new-password"
            minLength={12}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="field-help">
            At least 12 characters. Length is what protects an account, so a long phrase beats a
            short tangle of symbols.
          </span>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="signup-confirm">
            <span className="lbl-text">Confirm password</span>
          </label>
          <input
            id="signup-confirm"
            className={`inp${mismatch ? ' inp-invalid' : ''}`}
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={submit.isPending}>
          {submit.isPending ? 'Sending…' : 'Create account'}
        </button>

        <button className="btn btn-block" type="button" onClick={onGoToSignIn}>
          I already have an account
        </button>
      </form>

      <div className="notice">
        <IconMail size={14} />
        <span>Every account on the platform gets every released tool. There is nothing to choose.</span>
      </div>
    </AuthLayout>
  );
}
